import DashboardContext from 'contexts/DashboardContext';
import { useDataStreamConfig } from 'dashboard-engine/hooks/useDataStreamConfig';
import { DataStreamType } from 'dashboard-engine/util/getDataStreamType';
import { MutableRefObject, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDatasetContext } from 'ui/editor/dataStream/contexts/DatasetContext';
import { useTileEditorContext } from '../../contexts/TileEditorContext';
import { EditorSteps } from '../constants';
import TileEditorStepsContext from '../contexts/TileEditorStepsContext';
import { useDataStreamFilters } from '../hooks/useDataStreamFilters';
import { useDataStreamType } from '../hooks/useDataStreamType';
import { StepWithCondition, useTileEditorSteps } from '../hooks/useTileEditorSteps';
import { useTileEditorStore } from '../state/TileEditorStoreProvider';
import { useParametersStep } from './stepDefinitions/dataStreamConfigurationStep/useParametersStep.tsx';
import { useDataStreamStep } from './stepDefinitions/dataStreamStep/useDataStreamStep.tsx';
import { useMetadataStep } from './stepDefinitions/metadataStep/useMetadataStep.tsx';
import { useTileMetadataStep } from './stepDefinitions/metadataStep/useTileMetadataStep.tsx';
import { useObjectsStep } from './stepDefinitions/objectsStep/useObjectsStep.tsx';
import { useShapingStep } from './stepDefinitions/shapingStep/useShapingStep.tsx';
import { useTimeframeStep } from './stepDefinitions/timeframeStep/useTimeframeStep.tsx';
import { useQueryStep } from './stepDefinitions/useQueryStep';

const DSO_LINKED_PARAM = 'datastream';

const getInitialEditorStep = (dataStreamType: DataStreamType) => {
    let step = undefined;

    if (dataStreamType.isConfigurable) {
        step = EditorSteps.parameters;
    }

    if (dataStreamType.supportsScope) {
        step = EditorSteps.objects;
    }

    return step;
};

interface TileEditorStepsContextWrapperProps {
    previouslyDocked?: MutableRefObject<boolean>;
    previouslyDatasetMode?: MutableRefObject<boolean>;
    onDockEditorStep?: () => void;
    onReleaseEditorStep?: () => void;
}
export const TileEditorStepsContextWrapper: React.FC<TileEditorStepsContextWrapperProps> = ({
    previouslyDocked,
    previouslyDatasetMode,
    onDockEditorStep,
    onReleaseEditorStep,
    children
}) => {
    const [searchParams] = useSearchParams();
    const isLinkedFromDSO = Boolean(searchParams.get(DSO_LINKED_PARAM));

    const { tileConfig, setTileConfig } = useTileEditorContext();
    const { timeframe: inheritedTimeframe } = useContext(DashboardContext);

    const { config: datasetConfig, setConfig: setDatasetConfig, isEditingSQL } = useDatasetContext();
    const { data: datasetData, isConfigured } = useDataStreamConfig(datasetConfig);
    const { data: tileData } = useDataStreamConfig(tileConfig);
    const { dataStreamType, isLoading } = useDataStreamType(datasetConfig);

    const { dataStreamState, scope, storeCurrentStep, workspaceId, dispatch } = useTileEditorStore((s) => ({
        storeCurrentStep: s.currentStep,
        dataStreamState: s.dataStream,
        scope: s.scope,
        workspaceId: s.dashboard.workspaceId,
        dispatch: s.dispatch
    }));

    const dataStreamFilters = useDataStreamFilters({ dispatch, ...dataStreamState });
    const hasDataStream = Boolean(datasetConfig.dataStream);
    const isConfigurableDataStream = dataStreamType && dataStreamType.isConfigurable;

    const hasScope = Object.keys(datasetConfig.scope || {}).length > 0;

    const editorSteps: StepWithCondition<any, any>[] = [
        useDataStreamStep(dataStreamFilters, isEditingSQL),
        useObjectsStep({
            supportsScope: dataStreamType.supportsScope,
            isLoading,
            hasDataStream,
            storeCurrentStep: storeCurrentStep ?? EditorSteps.dataStream,
            datasetConfigScope: datasetConfig.scope,
            scopeState: scope,
            workspaceId,
            selectedDataStream: dataStreamState.selectedDataStream
        }),
        useParametersStep({
            isLoading,
            hasDataStream,
            isConfigurableDataStream,
            hasScope,
            supportsScope: dataStreamType.supportsScope
        }),
        useTimeframeStep({
            isLoading,
            hasDataStream,
            isConfigured,
            dataStreamId: datasetConfig.dataStream?.id,
            inheritedTimeframe,
            configTimeframe: datasetConfig.timeframe
        }),
        useShapingStep({
            isLoading,
            hasDataStream,
            isConfigured
        }),
        useMetadataStep({
            isLoading,
            hasDataStream,
            isConfigured,
            dataStreamMetadata: datasetConfig.dataStream?.metadata,
            columnNames: datasetData.metadata.columns.map((column) => column.name),
            config: datasetConfig,
            setConfig: setDatasetConfig
        }),
        useQueryStep({
            isEditingSQL
        }),
        useTileMetadataStep({
            isEditingSQL,
            dataStreamMetadata: tileConfig.dataStream?.metadata,
            columnNames: tileData.metadata.columns.map((column) => column.name),
            config: tileConfig,
            setConfig: setTileConfig
        })
    ];

    const stepsValue = useTileEditorSteps(
        editorSteps,
        isLinkedFromDSO
            ? getInitialEditorStep(dataStreamType)
            : isEditingSQL && previouslyDatasetMode && !previouslyDatasetMode?.current
            ? EditorSteps.query
            : undefined,
        isConfigured,
        isEditingSQL,
        previouslyDocked,
        dataStreamType,
        onDockEditorStep,
        onReleaseEditorStep,
        (newStep) => {
            if (newStep !== storeCurrentStep) {
                dispatch({ type: 'setActiveStep', step: newStep as EditorSteps });
            }
        }
    );

    useEffect(() => {
        if (previouslyDatasetMode?.current) {
            previouslyDatasetMode.current = isEditingSQL;
        }
    }, [isEditingSQL, previouslyDatasetMode]);

    return <TileEditorStepsContext.Provider value={stepsValue}>{children}</TileEditorStepsContext.Provider>;
};
